import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Gamepad2, Info, Plus, Search, Send, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { RolePicker } from '@/components/RolePicker';
import { useConfigPage, BackLink } from './_shared';

/**
 * Patch Notes (bot plugins/PatchNotes): which games, where they're posted,
 * who gets pinged. Stored as `moduleConfigs.patchNotes = { channelId, games }`
 * (bot lib/patchNotes.js reads it). The bot checks every 10 minutes and
 * starts with the next new post; "Test" posts the newest one right away from
 * the saved settings.
 */
interface PatchGame {
  source: 'steam' | 'genshin';
  appId?: number;
  name: string;
  image?: string | null;
  channelId?: string | null;
  roleId?: string | null;
  allAnnouncements?: boolean;
}

const MAX_GAMES = 25;
const CHANNEL_TYPES = [CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT];

const steamHeader = (appId: number) =>
  `https://shared.akamai.steamstatic.com/store_item_assets/steam/apps/${appId}/header.jpg`;
const steam = (appId: number, name: string): PatchGame => ({ source: 'steam', appId, name, image: steamHeader(appId) });

/** The server's games (Self-Roles) that have an official source. */
const SUGGESTED: PatchGame[] = [
  steam(381210, 'Dead by Daylight'),
  steam(236390, 'War Thunder'),
  steam(359550, 'Rainbow Six Siege'),
  steam(1222670, 'Die Sims 4'),
  steam(3241660, 'R.E.P.O.'),
  steam(1623730, 'Palworld'),
  steam(730, 'Counter-Strike 2'),
  steam(2807960, 'Battlefield 6'),
  { source: 'genshin', name: 'Genshin Impact', image: null },
];

const keyOf = (g: PatchGame) => (g.source === 'genshin' ? 'genshin' : `steam:${g.appId}`);

function readGames(raw: unknown): PatchGame[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (g): g is PatchGame => !!g && typeof g === 'object' && (g.source === 'genshin' || (g.source === 'steam' && Number.isInteger(g.appId)))
  );
}

export function PatchNotesConfig() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('patchNotes');
  const [channelId, setChannelId] = useState<string | null>(null);
  const [games, setGames] = useState<PatchGame[]>([]);

  const saved = {
    channelId: (currentConfig.channelId as string) ?? null,
    games: readGames(currentConfig.games),
  };
  useEffect(() => {
    if (settingsQuery.data) {
      setChannelId(saved.channelId);
      setGames(saved.games);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  // The test button works off the saved settings.
  const dirty = JSON.stringify({ channelId, games }) !== JSON.stringify(saved);
  const savedKeys = new Set(saved.games.map(keyOf));
  const added = new Set(games.map(keyOf));
  const suggestions = SUGGESTED.filter((g) => !added.has(keyOf(g)));

  const add = (list: PatchGame[]) =>
    setGames((prev) => {
      const have = new Set(prev.map(keyOf));
      return [...prev, ...list.filter((g) => !have.has(keyOf(g)))].slice(0, MAX_GAMES);
    });
  const update = (key: string, patch: Partial<PatchGame>) =>
    setGames((prev) => prev.map((g) => (keyOf(g) === key ? { ...g, ...patch } : g)));
  const remove = (key: string) => setGames((prev) => prev.filter((g) => keyOf(g) !== key));

  const testMut = useMutation({
    mutationFn: (gameKey: string) =>
      api('/api/actions/patch-notes-test', { method: 'POST', body: JSON.stringify({ guildId, gameKey }) }),
  });

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  const withoutChannel = !channelId && games.some((g) => !g.channelId);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <BackLink guildId={guildId} />
      <h1 className="text-2xl font-semibold">Patch Notes</h1>
      <p className="text-sm text-muted-foreground">
        Der Bot schaut alle 10 Minuten nach neuen Patch Notes und postet sie mit den ersten Zeilen und einem Link
        zum Original. Los geht es mit dem nächsten neuen Beitrag — ältere werden nicht nachgeholt.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Standard-Kanal</CardTitle>
          <CardDescription>Hierhin kommen alle Spiele, die keinen eigenen Kanal haben.</CardDescription>
        </CardHeader>
        <CardContent>
          <ChannelPicker guildId={guildId} value={channelId} onChange={setChannelId} types={CHANNEL_TYPES} />
          {withoutChannel && games.length > 0 && (
            <p className="mt-2 text-xs text-amber-500">
              Ohne Standard-Kanal posten nur Spiele, die unten einen eigenen Kanal haben.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Spiele ({games.length})</CardTitle>
          <CardDescription>
            Standard: nur Patch Notes und Updates. „Alle Ankündigungen“ nimmt auch Events, neue Inhalte und Angebote
            mit — Presseartikel nie.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {games.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Spiele — unten aus den Vorschlägen oder per Suche hinzufügen.</p>
          )}
          {games.map((g) => {
            const key = keyOf(g);
            return (
              <div key={key} className="space-y-3 rounded-md border p-3">
                {/* Narrow screens: the buttons wrap below rather than squeezing the name. */}
                <div className="flex flex-wrap items-center gap-3">
                  <GameImage game={g} className="h-9 w-[78px] sm:h-12 sm:w-[104px]" />
                  <div className="min-w-[8rem] flex-1">
                    <div className="truncate font-medium">{g.name}</div>
                    <div className="text-xs text-muted-foreground">{g.source === 'genshin' ? 'HoYoverse' : 'Steam'}</div>
                  </div>
                  <div className="flex items-center gap-1 max-sm:ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={dirty || !savedKeys.has(key) || testMut.isPending}
                      title={dirty ? 'Erst speichern — der Test nutzt die gespeicherten Einstellungen' : 'Neuesten Beitrag jetzt posten'}
                      onClick={() => testMut.mutate(key)}
                    >
                      <Send className="h-3.5 w-3.5" /> Test
                    </Button>
                    <Button variant="ghost" size="icon" title="Entfernen" onClick={() => remove(key)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs">Kanal</Label>
                    <ChannelPicker
                      guildId={guildId}
                      value={g.channelId ?? null}
                      onChange={(id) => update(key, { channelId: id })}
                      types={CHANNEL_TYPES}
                      placeholder="— Standard-Kanal —"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Rolle pingen</Label>
                    <RolePicker
                      guildId={guildId}
                      value={g.roleId ?? null}
                      onChange={(id) => update(key, { roleId: id })}
                      placeholder="— niemand —"
                      hideEveryone
                    />
                  </div>
                </div>
                <Switch
                  checked={!!g.allAnnouncements}
                  onChange={(e) => update(key, { allAnnouncements: e.target.checked })}
                  label="Alle offiziellen Ankündigungen (nicht nur Patch Notes)"
                />
              </div>
            );
          })}
          {testMut.isSuccess && !testMut.isPending && (
            <p className="text-sm text-primary">Test geschickt — das Ergebnis erscheint gleich als Meldung.</p>
          )}
          {testMut.error && <p className="text-sm text-destructive">Fehler: {(testMut.error as Error).message}</p>}
        </CardContent>
      </Card>

      {suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="text-lg">Vorschläge</CardTitle>
                <CardDescription>Eure Spiele mit offiziellen Patch Notes.</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => add(suggestions)}>
                <Plus className="h-4 w-4" /> Alle hinzufügen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {suggestions.map((g) => (
              <button
                key={keyOf(g)}
                type="button"
                onClick={() => add([g])}
                className="flex items-center gap-3 rounded-md border p-2 text-left text-sm transition-colors hover:border-primary"
              >
                <GameImage game={g} className="h-9 w-[78px]" />
                <span className="min-w-0 flex-1 truncate">{g.name}</span>
                <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      <SteamSearch guildId={guildId} added={added} onAdd={(g) => add([g])} full={games.length >= MAX_GAMES} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Info className="h-4 w-4 text-primary" /> Valorant, Fortnite, Star Stable, Alicia
          </CardTitle>
          <CardDescription>
            Diese Spiele bieten keine Schnittstelle für Patch Notes. Discord kann ihre Ankündigungen aber direkt in
            euren Server weiterleiten:
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>Dem offiziellen Discord-Server des Spiels beitreten.</li>
            <li>Dort den Ankündigungskanal öffnen (Megafon-Symbol 📢 vor dem Namen).</li>
            <li>Oben auf „Folgen“ klicken, diesen Server und den Patch-Notes-Kanal wählen.</li>
          </ol>
          <p className="mt-2 text-xs text-muted-foreground">
            Dafür braucht man auf diesem Server das Recht „Webhooks verwalten“.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {saveMut.isSuccess && !dirty && <span className="text-sm text-success">Gespeichert ✓</span>}
        {saveMut.error && (
          <span className="text-sm text-destructive">Fehler: {(saveMut.error as Error).message}</span>
        )}
        <Button disabled={saveMut.isPending || !dirty} onClick={() => saveMut.mutate({ moduleConfig: { channelId, games } })}>
          {saveMut.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
}

function GameImage({ game, className }: { game: PatchGame; className?: string }) {
  const [broken, setBroken] = useState(false);
  if (!game.image || broken) {
    return (
      <div className={`flex shrink-0 items-center justify-center rounded bg-muted ${className ?? ''}`}>
        <Gamepad2 className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={game.image}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className={`shrink-0 rounded object-cover ${className ?? ''}`}
    />
  );
}

interface SteamResult {
  appId: number;
  name: string;
  image: string;
}

function SteamSearch({
  guildId,
  added,
  onAdd,
  full,
}: {
  guildId: string;
  added: Set<string>;
  onAdd: (g: PatchGame) => void;
  full: boolean;
}) {
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 400);
    return () => clearTimeout(t);
  }, [text]);

  const search = useQuery({
    queryKey: ['patch-notes-steam', guildId, term],
    queryFn: () =>
      api<{ results: SteamResult[] }>(
        `/api/guilds/${guildId}/patch-notes/steam-search?q=${encodeURIComponent(term)}`
      ),
    enabled: term.length >= 2,
    staleTime: 5 * 60_000,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Weitere Steam-Spiele</CardTitle>
        <CardDescription>Jedes Spiel auf Steam geht — einfach suchen und hinzufügen.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Spiel suchen, z. B. Rust"
            className="pl-9"
          />
        </div>
        {search.isFetching && <p className="text-sm text-muted-foreground">Suche…</p>}
        {search.error && <p className="text-sm text-destructive">{(search.error as Error).message}</p>}
        {search.data && search.data.results.length === 0 && !search.isFetching && (
          <p className="text-sm text-muted-foreground">Nichts gefunden.</p>
        )}
        {search.data && search.data.results.length > 0 && (
          <ul className="divide-y rounded-md border">
            {search.data.results.map((r) => {
              const isAdded = added.has(`steam:${r.appId}`);
              return (
                <li key={r.appId} className="flex items-center gap-3 p-2">
                  <GameImage game={{ source: 'steam', appId: r.appId, name: r.name, image: r.image }} className="h-9 w-[78px]" />
                  <span className="min-w-0 flex-1 truncate text-sm">{r.name}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isAdded || full}
                    onClick={() => onAdd({ source: 'steam', appId: r.appId, name: r.name, image: r.image })}
                  >
                    {isAdded ? 'Hinzugefügt' : 'Hinzufügen'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        {full && <p className="text-xs text-muted-foreground">Höchstens {MAX_GAMES} Spiele.</p>}
      </CardContent>
    </Card>
  );
}
